import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, ChevronRight } from "lucide-react";

const NAV_LINKS = [
    { to: "/", label: "Home" },
    { to: "/catalogo", label: "Catalogo mezzi" },
    { to: "/preventivo", label: "Calcola preventivo" },
    { to: "/corsi", label: "Corsi & patentini" },
    { to: "/notifiche", label: "Notifiche e offerte" },
    { to: "/contatti", label: "Chi siamo / Contatti" },
];

const WhatsAppIcon = ({ className }) => (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.074-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.67-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
    </svg>
);

export const Footer = () => {
    return (
        <footer
            id="contatti"
            data-testid="footer-section"
            className="bg-gradient-to-b from-nsp-green to-nsp-green-dark text-white relative overflow-hidden"
        >
            <div
                aria-hidden="true"
                className="absolute -top-32 -right-32 w-96 h-96 bg-nsp-yellow/5 rounded-full blur-3xl"
            />

            <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-14 py-14 lg:py-20">
                <div className="grid sm:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-10">
                    {/* Brand */}
                    <div className="sm:col-span-2 lg:col-span-4">
                        <div className="flex items-center gap-4">
                            <div className="w-16 h-16 bg-white/95 rounded-2xl flex items-center justify-center p-2 shadow-lg">
                                <img
                                    src="/nsp-logo.png"
                                    alt="NSP Nolo Service Point"
                                    className="w-full h-full object-contain"
                                />
                            </div>
                            <div>
                                <div className="font-display text-xl uppercase tracking-tight">
                                    Nolo Service Point
                                </div>
                                <div className="text-[10px] uppercase tracking-[0.25em] text-nsp-yellow mt-0.5">
                                    BigMat Ediltutto
                                </div>
                            </div>
                        </div>
                        <p className="text-white/70 text-sm mt-6 leading-relaxed max-w-md">
                            Noleggio attrezzature da cantiere a 360° e corsi di formazione
                            con patentino. Una realtà storica del Lazio, vicino al tuo lavoro.
                        </p>
                        <div className="mt-6 flex flex-wrap gap-2">
                            <a
                                data-testid="footer-phone"
                                href="tel:3272237610"
                                className="rounded-full bg-nsp-yellow text-nsp-green px-4 py-2.5 font-bold text-xs flex items-center gap-2 hover:bg-white active:scale-95 transition-all shadow-lg shadow-nsp-yellow/20"
                            >
                                <Phone className="w-3.5 h-3.5" /> 327 2237610
                            </a>
                            <a
                                data-testid="footer-whatsapp"
                                href="https://wa.me/393272237610"
                                target="_blank"
                                rel="noreferrer"
                                className="rounded-full bg-[#25D366] text-white px-4 py-2.5 font-bold text-xs flex items-center gap-2 hover:bg-[#1ebe5b] active:scale-95 transition-all shadow-lg shadow-[#25D366]/20"
                            >
                                <WhatsAppIcon className="w-3.5 h-3.5 fill-current" />
                                Scrivici su WhatsApp
                            </a>
                        </div>
                    </div>

                    {/* Navigazione */}
                    <div className="lg:col-span-2">
                        <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-nsp-yellow mb-5">
                            Navigazione
                        </div>
                        <ul className="space-y-2.5">
                            {NAV_LINKS.map((l) => (
                                <li key={l.to}>
                                    <Link
                                        to={l.to}
                                        data-testid={`footer-nav-${l.to.replace("/", "") || "home"}`}
                                        className="group flex items-center gap-1.5 text-sm text-white/70 hover:text-nsp-yellow transition-colors"
                                    >
                                        <ChevronRight className="w-3.5 h-3.5 text-nsp-yellow/50 group-hover:translate-x-0.5 transition-transform" />
                                        {l.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Sede e orari */}
                    <div className="lg:col-span-3">
                        <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-nsp-yellow mb-5">
                            Sede & Orari
                        </div>
                        <div
                            data-testid="footer-sede-0"
                            className="flex items-start gap-3 bg-white/5 backdrop-blur-sm rounded-2xl p-4 border border-white/5"
                        >
                            <div className="w-9 h-9 rounded-xl bg-nsp-yellow/15 flex items-center justify-center flex-shrink-0">
                                <MapPin className="w-4 h-4 text-nsp-yellow" />
                            </div>
                            <div className="min-w-0">
                                <div className="font-bold text-sm">BigMat S.p.A. (Sede unica)</div>
                                <div className="text-white/70 text-sm mt-0.5">
                                    Via Salaria Vecchia 31
                                    <br />
                                    02037 Osteria Nuova di Poggio Moiano (RI)
                                </div>
                            </div>
                        </div>
                        <div className="flex items-start gap-3 mt-4 px-1">
                            <div className="w-8 h-8 rounded-lg bg-nsp-yellow/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <Clock className="w-3.5 h-3.5 text-nsp-yellow" />
                            </div>
                            <div>
                                <div className="text-white/50 text-[10px] uppercase tracking-widest">Orari</div>
                                <div className="text-white/80 text-xs mt-0.5 leading-relaxed">
                                    Lun-Ven: 8:00-18:00 · Sab: 8:00-13:00
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Contatti */}
                    <div className="lg:col-span-3">
                        <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-nsp-yellow mb-5">
                            Contatti
                        </div>
                        <div className="space-y-4 text-sm">
                            <ContactRow icon={Phone} label="Telefono (anche WhatsApp)" href="tel:3272237610" value="327 2237610" />
                            <RevealEmailRow />
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom strip */}
            <div className="relative bg-black/20 border-t border-white/5">
                <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-14 py-5 space-y-2">
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-3 text-xs text-white/50">
                        <div data-testid="footer-copyright">
                            © {new Date().getFullYear()} NSP Nolo Service Point Soc. Coop. · Tutti i diritti riservati
                        </div>
                        <div className="flex gap-5 uppercase tracking-wider">
                            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-nsp-yellow transition-colors">
                                Privacy
                            </Link>
                            <Link to="/privacy#cookie" data-testid="footer-cookie-link" className="hover:text-nsp-yellow transition-colors">
                                Cookie
                            </Link>
                        </div>
                    </div>
                    <div className="text-[11px] text-white/35 text-center sm:text-left leading-relaxed" data-testid="footer-legal">
                        Sede legale: Largo Montedinove 4 – 00138 Roma (RM) · Sede operativa: Via Salaria
                        Vecchia 29 – 02037 Osteria Nuova di Poggio Moiano (RI) · P.IVA / C.F. 12162641000 · REA RM-1355223
                    </div>
                </div>
            </div>
        </footer>
    );
};

/* Email assemblata da parti separate e mostrata solo al click: invisibile ai bot spam. */
const RevealEmailRow = () => {
    const [shown, setShown] = React.useState(false);
    const addr = ["info", "noloservicepoint", "it"];
    const email = `${addr[0]}@${addr[1]}.${addr[2]}`;
    return (
        <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-nsp-yellow/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Mail className="w-3.5 h-3.5 text-nsp-yellow" />
            </div>
            <div className="min-w-0">
                <div className="text-white/50 text-[10px] uppercase tracking-widest">Email</div>
                {shown ? (
                    <a
                        href={`mailto:${email}`}
                        data-testid="footer-email-revealed"
                        className="font-bold hover:text-nsp-yellow break-all text-sm block mt-0.5 transition-colors"
                    >
                        {email}
                    </a>
                ) : (
                    <button
                        type="button"
                        data-testid="footer-email-reveal"
                        onClick={() => setShown(true)}
                        className="mt-1 rounded-full bg-white/10 border border-white/20 text-white/85 text-xs font-bold px-3.5 py-1.5 hover:bg-nsp-yellow hover:text-nsp-green hover:border-nsp-yellow transition-colors active:scale-95"
                    >
                        Mostra email
                    </button>
                )}
            </div>
        </div>
    );
};

const ContactRow = ({ icon: Icon, label, href, value }) => (
    <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-nsp-yellow/15 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Icon className="w-3.5 h-3.5 text-nsp-yellow" />
        </div>
        <div className="min-w-0">
            <div className="text-white/50 text-[10px] uppercase tracking-widest">{label}</div>
            <a href={href} className="font-bold hover:text-nsp-yellow break-all text-sm block mt-0.5 transition-colors">
                {value}
            </a>
        </div>
    </div>
);

export default Footer;
